'use client';

import { Portal } from '@common/components';
import { cn } from '@core/utils';
import { motion } from 'framer-motion';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

import { DONATION_BNB_TOAST_ITEMS } from '@/app/donation/constants';
import {
  DONATION_ROUTES,
  ROUTE_TYPES,
  ROUTES,
  SELECTOR,
  SUB_DOMAIN,
} from '@/constants';
import { SAFE_AREA_BOTTOM_IOS } from '@/modules/wello-app/_hooks/useMountAppData';
import { useAuthStore } from '@/stores/AuthStore';
import { useDeviceInfoStore } from '@/stores/DeviceStore';
import { useGlobalStore } from '@/stores/GlobalStore';

import { DonationBnb } from './_components/DonationBnb';
import styles from './DonationBnbController.module.scss';

export const DonationBnbController = () => {
  const pathname = usePathname();
  const isLoading = useAuthStore((state) => state.isLogin === undefined);
  const currentRouteType = useGlobalStore((state) => state.currentRouteType);
  const hideBottomNav = useGlobalStore((state) => state.hideBottomNav);
  const [displayToast, setDisplayToast] = useState(isLoading);

  useEffect(() => {
    const mainElement = document.getElementById(SELECTOR.MAIN);
    if (!mainElement || !pathname) {
      setDisplayToast(false);

      return;
    }

    const isDisplayPath =
      DISPLAY_TOAST_PATHNAME.includes(pathname) ||
      (currentRouteType === ROUTE_TYPES.DONATION &&
        pathname === ROUTES.MY.pathname);

    setDisplayToast(isDisplayPath);
  }, [pathname, currentRouteType]);

  const shouldShow = displayToast && !hideBottomNav;

  const { isSafeAreaBottomVersion } = useDeviceInfoStore((state) => {
    return {
      isSafeAreaBottomVersion:
        state.subDomain === SUB_DOMAIN.WELLO_APP &&
        state.osType === 'ios' &&
        state.appVersion &&
        state.appVersion >= '3.1.10',
    };
  });

  return (
    <Portal renderTo={() => document.getElementById(SELECTOR.MAIN_WRAPPER)}>
      <motion.div
        animate={shouldShow ? 'show' : 'hide'}
        initial={isLoading ? 'show' : 'hide'}
        variants={{
          show: {
            opacity: 1,
            display: 'block',
          },
          hide: {
            opacity: 0,
            transitionEnd: {
              display: 'none',
            },
          },
        }}
        transition={{
          duration: 0.2,
        }}
        style={{
          bottom: isSafeAreaBottomVersion
            ? 10 + SAFE_AREA_BOTTOM_IOS
            : undefined,
        }}
        className={cn(styles.container, {
          'pointer-events-none': !shouldShow,
          'sr-only': isLoading,
        })}
      >
        <DonationBnb isMounted={shouldShow} />
      </motion.div>
    </Portal>
  );
};

const DISPLAY_TOAST_PATHNAME = [
  ...DONATION_BNB_TOAST_ITEMS.map(({ route }) => route.pathname),
  DONATION_ROUTES.GIFTS_SEARCH.pathname,
] as const;
